import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { advanceService, Advance } from '../../services/advance.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
];

export function AdvancesPage() {
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');

  const load = () => {
    setLoading(true);
    setError(null);
    advanceService
      .getAdvances()
      .then(setAdvances)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return advances;
    return advances.filter((a) => a.status === filter);
  }, [advances, filter]);

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Avances de Obra</h1>
          <p className="text-xs text-slate-500">{advances.length} registros</p>
        </div>
        <Link
          to="/gestion/avance-obra"
          className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
        >
          + Registrar
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              filter === f.value
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="📊"
          title="Sin avances"
          description={
            advances.length === 0
              ? 'Registra el primer avance desde el botón de arriba.'
              : 'No hay avances con este filtro.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {filtered.map((a) => (
            <li key={a.id}>
              <Link
                to={`/gestion/avances/${a.id}`}
                className="block bg-white rounded-xl shadow-sm p-4 active:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      {a.project_name ?? a.project_id.slice(0, 8)}
                    </h3>
                    <p className="text-xs text-slate-500">
                      Semana {a.week_number} · {a.year}
                    </p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>

                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-semibold text-primary">
                    {a.progress_percentage}%
                  </span>
                  {a.contractor_name && (
                    <span className="truncate">{a.contractor_name}</span>
                  )}
                  {a.photos.length > 0 && (
                    <span>📷 {a.photos.length}</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}